import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  DEFAULT_TIMEZONE,
  deserializeNodeIdToAnswerPathMap,
  getAllSubsections,
  Localized,
  NodeIdToAnswerPathMap,
  PivotAnswerResolver,
  QuestionnaireBlueprint,
  QuestionnaireDefinition,
  QuestionnaireEngine,
  RenderingQuestionnaire,
  RenderingType,
  SerializedNodeIdToAnswerPathMap,
  Timezone,
  VersionedAnswers,
} from '@zinnia/form-engine-sdk';

import { DebugToolbarModal, Loader } from '@breathelife/ui-components';

import { navigationSlice } from '@breathelife/redux';
import { Language } from '@breathelife/types';
import { useCxSelector } from '../../Hooks/useCxSelector';
import { generateQuestionWithStepIdUrl } from '../../Navigation/Urls';
import { updateAnswersUntil } from '../../Redux/DebugToolbar/DebugToolbarOperations';
import { notificationSlice } from '../../Redux/Notification/NotificationSlice';
import ApiService from '../../Services/ApiService';

export type QuestionnaireResponse = {
  version: string;
  questionnaire: Localized<QuestionnaireDefinition>;
  nodeIdToAnswerPath?: SerializedNodeIdToAnswerPathMap;
};

export enum DebugView {
  stepList = 'stepList',
  stepFill = 'stepFill',
}

type Props = {
  debugView: DebugView;
  onClose: () => void;
};

export function DebugToolbarModalContainer(props: Props): ReactElement {
  const { debugView, onClose } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [questionnaire, setQuestionnaire] = useState<Localized<QuestionnaireDefinition>>();
  const [renderingQuestionnaire, setRenderingQuestionnaire] = useState<RenderingQuestionnaire>();
  const [currentQuestionnaireApplicationId, setCurrentQuestionnaireApplicationId] = useState<string>();
  const [nodeIdToAnswerPathMap, setNodeIdToAnswerPathMap] = useState<NodeIdToAnswerPathMap>();
  const [isFetchingQuestionnaire, setIsFetchingQuestionnaire] = useState(false);
  const applicationId = useCxSelector((store) => store.consumerFlow.insuranceApplication.insuranceApplication?.id);
  const blueprint = useCxSelector((store) => store.consumerFlow.insuranceApplication.insuranceApplication?.blueprint);

  useEffect(() => {
    const isSameApplicationId = currentQuestionnaireApplicationId === applicationId;
    if (isSameApplicationId || isFetchingQuestionnaire) return;

    async function fetchQuestionnaire(): Promise<void> {
      if (!blueprint || !applicationId) return;

      setIsFetchingQuestionnaire(true);

      try {
        const response = await ApiService.getCarrierQuestionnaire<QuestionnaireResponse>(applicationId);
        const { questionnaire, nodeIdToAnswerPath: serializedNodeIdToAnswerPathMap } = response.data;
        if (!questionnaire || !serializedNodeIdToAnswerPathMap) {
          throw new Error('Missing questionnaire or nodeIdToAnswerPath in the response');
        }

        setCurrentQuestionnaireApplicationId(applicationId);
        setQuestionnaire(questionnaire);
        setNodeIdToAnswerPathMap(deserializeNodeIdToAnswerPathMap(serializedNodeIdToAnswerPathMap));

        const nodeIdToAnswerPathMap = deserializeNodeIdToAnswerPathMap(serializedNodeIdToAnswerPathMap);

        const timezone = Timezone.from(Intl.DateTimeFormat().resolvedOptions().timeZone).withDefault(DEFAULT_TIMEZONE);

        setRenderingQuestionnaire(
          buildRenderingQuestionnaire(questionnaire, blueprint, nodeIdToAnswerPathMap, timezone, null),
        );
      } catch (err: any) {
        // No need to translate since this is only for internal use.
        dispatch(
          notificationSlice.actions.setError({
            message: 'An error occurred while fetching the questionnaire for the debug toolbar',
          }),
        );
      } finally {
        setIsFetchingQuestionnaire(false);
      }
    }
    void fetchQuestionnaire();
  }, [
    dispatch,
    questionnaire,
    nodeIdToAnswerPathMap,
    isFetchingQuestionnaire,
    applicationId,
    currentQuestionnaireApplicationId,
    blueprint,
  ]);

  const steps = useMemo(() => {
    if (!renderingQuestionnaire) return;
    // TODO: Handle steps that are not visible
    const subsectionsById = getAllSubsections(renderingQuestionnaire).map((subsection) => subsection.id);
    return subsectionsById;
  }, [renderingQuestionnaire]);

  const onNavigate = useCallback(
    (stepId: string) => {
      dispatch(navigationSlice.actions.setLoadingPage({ isVisible: false }));
      const url = generateQuestionWithStepIdUrl(stepId);
      navigate(url);
    },
    [navigate, dispatch],
  );

  const onPrefillApplication = useCallback(
    (stepId: string) => {
      if (!applicationId) return;
      dispatch(updateAnswersUntil(applicationId, stepId, navigate));
      onClose();
    },
    [applicationId, dispatch, onClose, navigate],
  );

  const onStepSelect = useCallback(
    (stepId: string) => {
      debugView === DebugView.stepFill ? onPrefillApplication(stepId) : onNavigate(stepId);
      onClose();
    },
    [debugView, onPrefillApplication, onNavigate, onClose],
  );

  if (isFetchingQuestionnaire) return <Loader />;

  return (
    <DebugToolbarModal
      onClose={onClose}
      steps={steps ?? []}
      onStepSelect={onStepSelect}
      title={debugView === DebugView.stepFill ? 'Prefill the application' : 'Jump to step'}
    />
  );
}

function buildRenderingQuestionnaire(
  questionnaire: Localized<QuestionnaireDefinition>,
  blueprint: QuestionnaireBlueprint,
  nodeIdToAnswerPathMap: NodeIdToAnswerPathMap,
  timezone: Timezone,
  currentDateOverride: string | null,
): RenderingQuestionnaire {
  const answerResolver = PivotAnswerResolver.from(
    new VersionedAnswers({ v1: {}, v2: {} }),
    nodeIdToAnswerPathMap,
    blueprint, // TODO: load the blueprint before merging, hum it was already using DEFAULT_BLUEPRINT below weird. Like it does not matter.
    'v1',
  );

  const questionnaireEngine = QuestionnaireEngine.from({
    blueprint,
    questionnaire,
    answerResolver,
    timezone,
    answerChangedSubscribers: [],
    onRenderingQuestionnaireChanged: () => {},
    language: Language.en, // TODO: update to real language used by the user
    renderingOptions: {
      // TODO: Remove filter
      renderingType: RenderingType.consumerSummary,
      displayErrors: false,
    },
    currentDateOverride,
  });

  return questionnaireEngine.renderingQuestionnaire;
}
